import { config } from '../config';
import { tokenManager } from '../auth/tokenManager';

export interface FriendGroup {
  id: number;
  user_id: number;
  name: string;
  description?: string;
  color: string;
  icon?: string;
  is_default?: boolean;
  auto_friend_members?: boolean;
  display_order: number;
  member_count: number;
  created_at: string;
  updated_at: string;
}

export interface CreateFriendGroupData {
  name: string;
  description?: string;
  color: string;
  icon?: string;
  display_order?: number;
  auto_friend_members?: boolean;
}

export interface UpdateFriendGroupData {
  name?: string;
  description?: string;
  color?: string;
  icon?: string;
  display_order?: number;
  auto_friend_members?: boolean;
}

export interface FriendGroupMember {
  friend_id: number;
  friend_full_name: string;
  profile_picture?: number;
  birthday?: string;
}

export const groupsApi = {
  getFriendGroups: async (userId: number): Promise<FriendGroup[]> => {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/user/${userId}/friend-groups`);
    
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }
    
    return response.json();
  },

  createFriendGroup: async (userId: number, groupData: CreateFriendGroupData): Promise<FriendGroup> => {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/user/${userId}/friend-groups`, {
      method: 'POST',
      body: JSON.stringify(groupData),
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }

    return response.json();
  },

  updateFriendGroup: async (userId: number, groupId: number, groupData: UpdateFriendGroupData): Promise<FriendGroup> => {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/user/${userId}/friend-groups/${groupId}`, {
      method: 'PUT',
      body: JSON.stringify(groupData),
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }

    return response.json();
  },

  deleteFriendGroup: async (userId: number, groupId: number): Promise<void> => {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/user/${userId}/friend-groups/${groupId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }
  },

  addFriendToGroup: async (userId: number, groupId: number, friendId: number): Promise<void> => {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/user/${userId}/friend-groups/${groupId}/members`, {
      method: 'POST',
      body: JSON.stringify({ friend_id: friendId }),
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }
  },

  removeFriendFromGroup: async (userId: number, groupId: number, friendId: number): Promise<void> => {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/user/${userId}/friend-groups/${groupId}/members/${friendId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }
  },

  updateFriendGroups: async (userId: number, friendId: number, groupIds: number[]): Promise<void> => {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/user/${userId}/friends/${friendId}/groups`, {
      method: 'PUT',
      body: JSON.stringify({ group_ids: groupIds }),
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }
  },

  getFriendsGrouped: async (userId: number, groupId?: number): Promise<any[]> => {
    const url = groupId 
      ? `${config.API_URL}/user/${userId}/friends/grouped?group_id=${groupId}`
      : `${config.API_URL}/user/${userId}/friends/grouped`;
    
    const response = await tokenManager.makeAuthenticatedRequest(url);
    
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }
    
    return response.json();
  },

  getGroupMembers: async (userId: number, groupId: number): Promise<FriendGroupMember[]> => {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/user/${userId}/friend-groups/${groupId}/members`);
    
    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }
    
    return response.json();
  },
};